import { lockScroll, unlockScroll, isTouchDevice, prefersReducedMotion } from '@theme/utilities';

/**
 * TSC · Exit-intent popup (sections/tsc-exit-popup.liquid).
 *
 * LIFECYCLE
 * ---------
 * 1. Page load: if the page is the reload after our form posted (Liquid renders a
 *    data-tsc-posted marker and we left a "pending" flag in sessionStorage), open
 *    straight onto the success screen (or back onto the form with Shopify's error).
 * 2. Otherwise, after the configured delay, arm the exit triggers:
 *      desktop  — cursor leaves through the top of the window
 *      mobile   — fast scroll-up, and/or the first back-button press
 *      optional — plain timer
 * 3. Submitting posts Shopify's native customer form (hCaptcha-protected, so it is a
 *    real navigation). The phone number rides along as a `phone:<digits>` tag.
 *
 * MEMORY (localStorage "tsc-exit-popup")
 * -------
 *   done        — signed up: never show again
 *   dismissedAt — closed without signing up: hide for `hideDays`
 * Testing mode (section setting) ignores both; ?tsc_popup=1 forces it open.
 */

const STORAGE_KEY = 'tsc-exit-popup';
const PENDING_KEY = 'tsc-exit-popup:pending';
const PENDING_TTL = 15 * 60 * 1000;

function readMemory() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeMemory(patch) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readMemory(), ...patch }));
  } catch {
    /* private mode — best effort */
  }
}

function readPending() {
  try {
    const stamp = Number(sessionStorage.getItem(PENDING_KEY) || 0);
    return stamp > 0 && Date.now() - stamp < PENDING_TTL;
  } catch {
    return false;
  }
}

function setPending(on) {
  try {
    if (on) sessionStorage.setItem(PENDING_KEY, String(Date.now()));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* best effort */
  }
}

/** Digits only (a leading + is implied by the country code). 7–15 digits per E.164. */
function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D+/g, '');
  if (digits.length < 7 || digits.length > 15) return '';
  return digits;
}

function rootPath() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function anotherOverlayIsOpen(ownDialog) {
  return Array.from(document.querySelectorAll('dialog[open], details[open][scroll-lock]')).some(
    (el) => el !== ownDialog
  );
}

class TscExitPopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  config = {};
  state = 'form';
  opened = false;
  armed = false;
  pushedHistoryEntry = false;
  ignoreNextPop = false;
  scrollSamples = [];
  timers = [];
  isTouch = false;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    try {
      this.config = JSON.parse(this.dataset.config || '{}');
    } catch {
      this.config = {};
    }

    this.isTouch = isTouchDevice() || window.innerWidth < 750;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    this.#bindUI();

    if (this.designMode) {
      this.#bindEditor();
      return;
    }

    if (this.#handlePostback()) return;

    if (new URLSearchParams(location.search).has('tsc_popup')) {
      this.open('forced');
      return;
    }

    if (!this.#eligible()) return;

    this.#armTriggers();
  }

  disconnectedCallback() {
    this.#disarm();
    this.#unbindEditor();
    if (this.dialog) unlockScroll(this.dialog);
  }

  /* ------------------------------------------------------------------ */
  /* Eligibility                                                          */
  /* ------------------------------------------------------------------ */

  #eligible() {
    if (this.config.testMode) return true;
    const memory = readMemory();
    if (memory.done) return false;
    const hideMs = Number(this.config.hideDays || 7) * 24 * 60 * 60 * 1000;
    if (memory.dismissedAt && Date.now() - memory.dismissedAt < hideMs) return false;
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Reload after the form posted                                         */
  /* ------------------------------------------------------------------ */

  #handlePostback() {
    const marker = this.querySelector('[data-tsc-posted]');
    const posted = marker ? marker.dataset.tscPosted : '';
    const pending = readPending();

    // A posted marker that isn't ours (e.g. the footer signup) — or a stale flag.
    if (!posted || !pending) {
      if (!posted) setPending(false);
      return false;
    }

    setPending(false);

    if (posted === 'success') {
      writeMemory({ done: true, doneAt: Date.now() });
      this.#setState('success');
      this.open('postback');
      this.#cleanUrl();
      this.#scrollPageToTop(); // Shopify lands on #contact_form (the footer signup) after posting
      this.#applyDiscountToCart();
      return true;
    }

    if (posted === 'error') {
      this.#setState('form');
      this.open('postback-error');
      return true;
    }

    return false;
  }

  #cleanUrl() {
    try {
      const url = new URL(location.href);
      if (!url.searchParams.has('customer_posted')) return;
      url.searchParams.delete('customer_posted');
      url.hash = '';
      history.replaceState(history.state, '', url.toString());
    } catch {
      /* ignore */
    }
  }

  /** The theme scrolls `.page-wrapper` on desktop and the window on mobile. */
  #scrollPageToTop() {
    try {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) wrapper.scrollTo({ top: 0, behavior: 'instant' });
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Triggers                                                             */
  /* ------------------------------------------------------------------ */

  #armTriggers() {
    const delay = Math.max(0, Number(this.config.delay || 0)) * 1000;

    this.timers.push(
      setTimeout(() => {
        this.armed = true;

        if (this.config.desktopExit !== false) {
          document.addEventListener('mouseout', this.#onMouseOut);
        }

        if (this.isTouch) {
          if (this.config.mobileScroll !== false) {
            window.addEventListener('scroll', this.#onScroll, { passive: true });
          }

          if (this.config.mobileBack !== false) {
            try {
              history.pushState({ tscExitPopup: true }, '', location.href);
              this.pushedHistoryEntry = true;
              window.addEventListener('popstate', this.#onPopState);
            } catch {
              /* history API unavailable */
            }
          }
        }

        const fallback = Number(this.config.fallbackSeconds || 0);
        if (fallback > 0) {
          this.timers.push(setTimeout(() => this.trigger('timer'), fallback * 1000));
        }
      }, delay)
    );
  }

  #disarm() {
    this.armed = false;
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers = [];
    document.removeEventListener('mouseout', this.#onMouseOut);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('popstate', this.#onPopState);
  }

  /** Cursor left the document through the top edge (toward tabs / address bar). */
  #onMouseOut = (event) => {
    if (event.relatedTarget || event.toElement) return;
    if (event.clientY > 10) return;
    const active = document.activeElement;
    if (active && /^(input|textarea|select)$/i.test(active.tagName)) return;
    this.trigger('exit-intent');
  };

  /** Fast upward flick on touch devices (reaching for the browser bar / back). */
  #onScroll = () => {
    const now = performance.now();
    const y = window.scrollY;
    this.scrollSamples.push({ t: now, y });
    this.scrollSamples = this.scrollSamples.filter((sample) => now - sample.t <= 450);
    this.maxScroll = Math.max(this.maxScroll || 0, y);

    if (this.maxScroll < window.innerHeight * 0.6) return;

    const oldest = this.scrollSamples[0];
    if (oldest && oldest.y - y >= 220) {
      this.scrollSamples = [];
      this.trigger('scroll-up');
    }
  };

  /** First back press shows the popup instead of leaving. */
  #onPopState = () => {
    if (this.ignoreNextPop) {
      this.ignoreNextPop = false;
      return;
    }
    this.pushedHistoryEntry = false;
    this.trigger('back-button');
  };

  trigger(reason) {
    if (!this.armed || this.opened) return;
    if (!this.dialog || anotherOverlayIsOpen(this.dialog)) return;
    if (!this.#eligible()) {
      this.#disarm();
      return;
    }
    this.open(reason);
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                         */
  /* ------------------------------------------------------------------ */

  open(reason = 'manual') {
    const { dialog } = this;
    if (!dialog || dialog.open) return;

    this.opened = true;
    this.#disarm();

    // We consumed the exit triggers by other means: drop the spare history entry
    // so the next back press really leaves.
    if (this.pushedHistoryEntry && reason !== 'back-button') {
      this.ignoreNextPop = true;
      this.pushedHistoryEntry = false;
      try {
        history.back();
      } catch {
        this.ignoreNextPop = false;
      }
    }

    dialog.classList.remove('is-closing');
    lockScroll(dialog);
    dialog.showModal();
    writeMemory({ lastShownAt: Date.now(), lastReason: reason });

    const focusTarget =
      this.state === 'success'
        ? this.querySelector('[data-tsc-copy]')
        : this.querySelector('#tsc-exit-popup-email');
    if (focusTarget instanceof HTMLElement) {
      requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }
  }

  async close({ remember = true } = {}) {
    const { dialog } = this;
    if (!dialog || !dialog.open || this.closing) return;

    this.closing = true;
    this.rememberOnClose = remember;

    if (!prefersReducedMotion()) {
      dialog.classList.add('is-closing');
      await new Promise((resolve) => {
        const done = () => {
          dialog.removeEventListener('animationend', done);
          resolve();
        };
        dialog.addEventListener('animationend', done, { once: true });
        setTimeout(done, 260);
      });
    }

    dialog.classList.remove('is-closing');
    dialog.close(); // fires the native `close` event → #onNativeClose
  }

  /** Runs for every way the dialog can close (our close(), Escape, browser close-watcher). */
  #onNativeClose = () => {
    const { dialog } = this;
    const remember = this.rememberOnClose !== false;
    this.rememberOnClose = true;
    this.closing = false;
    this.opened = false;

    if (dialog) {
      dialog.classList.remove('is-closing');
      unlockScroll(dialog);
    }

    if (remember && !this.designMode) {
      if (this.state === 'success') writeMemory({ done: true });
      else writeMemory({ dismissedAt: Date.now() });
    }
  };

  #setState(state) {
    this.state = state;
    if (this.dialog) this.dialog.dataset.state = state;
    this.querySelectorAll('[data-tsc-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.tscPanel !== state;
    });
  }

  /* ------------------------------------------------------------------ */
  /* UI wiring                                                            */
  /* ------------------------------------------------------------------ */

  #bindUI() {
    const { dialog } = this;
    if (!dialog) return;

    this.querySelectorAll('[data-tsc-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Backdrop click
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) this.close();
    });

    // Escape / browser close-watcher / our own close() all end here.
    dialog.addEventListener('close', this.#onNativeClose);

    const form = this.querySelector('form');
    const phone = this.querySelector('[data-tsc-phone]');
    const tags = this.querySelector('[data-tsc-tags]');
    const phoneError = this.querySelector('[data-tsc-phone-error]');
    const submit = this.querySelector('[data-tsc-submit]');

    const syncTags = () => {
      if (!(phone instanceof HTMLInputElement) || !(tags instanceof HTMLInputElement)) return;
      const base = String(this.config.baseTags || 'exit-popup')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      const digits = normalizePhone(phone.value);
      if (digits) base.push(`phone:${digits}`);
      tags.value = base.join(',');
    };

    const phoneIsValid = () => {
      if (!(phone instanceof HTMLInputElement)) return true;
      if (normalizePhone(phone.value)) return true;
      phone.setAttribute('aria-invalid', 'true');
      if (phoneError) {
        phoneError.textContent = this.config.phoneError || 'Please enter a valid phone number.';
        phoneError.hidden = false;
      }
      phone.focus();
      return false;
    };

    if (phone instanceof HTMLInputElement) {
      phone.addEventListener('input', () => {
        syncTags();
        if (phoneError) phoneError.hidden = true;
        phone.removeAttribute('aria-invalid');
      });
    }

    // Validate on the button click so nothing downstream (Shopify's captcha
    // handler included) ever sees a submit with a bad phone number. The
    // pending flag is set here too, in case the captcha handler swallows the
    // submit event before our listener below runs.
    if (submit instanceof HTMLButtonElement) {
      submit.addEventListener('click', (event) => {
        if (!phoneIsValid()) {
          event.preventDefault();
          return;
        }
        syncTags();
        setPending(true);
      });
    }

    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', (event) => {
        if (!phoneIsValid()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        syncTags();
        setPending(true);

        if (submit instanceof HTMLButtonElement) {
          submit.setAttribute('aria-busy', 'true');
        }
        // Not preventing default: Shopify's captcha script takes over the native submit.
      });
    }

    const copy = this.querySelector('[data-tsc-copy]');
    const codeEl = this.querySelector('[data-tsc-code]');
    if (copy instanceof HTMLButtonElement && codeEl) {
      copy.addEventListener('click', async () => {
        const code = (codeEl.textContent || this.config.code || '').trim();
        try {
          await navigator.clipboard.writeText(code);
        } catch {
          const range = document.createRange();
          range.selectNodeContents(codeEl);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
          try {
            document.execCommand('copy');
          } catch {
            /* unsupported */
          }
        }
        copy.textContent = this.config.copiedLabel || 'Copied!';
        copy.classList.add('is-copied');
        setTimeout(() => {
          copy.textContent = this.config.copyLabel || 'Copy';
          copy.classList.remove('is-copied');
        }, 2200);
      });
    }

    const cta = this.querySelector('[data-tsc-cta]');
    if (cta) {
      cta.addEventListener('click', () => writeMemory({ done: true }));
    }
  }

  /** Theme editor: open on select / reload, close on deselect, preview either screen. */
  #bindEditor() {
    const sectionId = this.dataset.sectionId;
    const isMine = (event) => {
      const detail = event.detail || {};
      return detail.sectionId === sectionId || (event.target instanceof Element && event.target.contains(this));
    };

    const onSelect = (event) => {
      if (!isMine(event)) return;
      this.#setState(this.config.previewState === 'success' ? 'success' : 'form');
      this.open('editor');
    };
    const onDeselect = (event) => {
      if (!isMine(event)) return;
      this.close({ remember: false });
    };

    this.editorListeners = [
      ['shopify:section:select', onSelect],
      ['shopify:section:load', onSelect],
      ['shopify:section:deselect', onDeselect],
    ];
    this.editorListeners.forEach(([name, handler]) => document.addEventListener(name, handler));
  }

  #unbindEditor() {
    (this.editorListeners || []).forEach(([name, handler]) => document.removeEventListener(name, handler));
    this.editorListeners = [];
  }

  /* ------------------------------------------------------------------ */
  /* Discount pre-apply (best effort — the CTA link also applies it)      */
  /* ------------------------------------------------------------------ */

  async #applyDiscountToCart() {
    const code = String(this.config.code || '').trim();
    if (!code) return;

    const codes = Array.from(document.querySelectorAll('.cart-discount__pill'))
      .map((pill) => pill.dataset.discountCode)
      .filter(Boolean);
    if (!codes.includes(code)) codes.push(code);

    const apply = async () => {
      try {
        await fetch(`${rootPath()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') }),
        });
      } catch {
        /* the /discount/CODE link on the CTA covers this */
      }
    };

    // Share the site-wide cart operation chain (cart-rewards.js, bundle-discount.js)
    // so this write never interleaves with theirs.
    const previous = window.__cartOpChain || Promise.resolve();
    const next = previous.catch(() => {}).then(apply);
    window.__cartOpChain = next.catch(() => {});
    await next;
  }
}

if (!customElements.get('tsc-exit-popup')) {
  customElements.define('tsc-exit-popup', TscExitPopup);
}
